import type { Temporal } from 'temporal-polyfill';
import type { Weekday } from './types';

export interface MonthGrid {
  readonly yearMonth: Temporal.PlainYearMonth;
  /** Whole weeks covering the month, each of exactly 7 dates. */
  readonly weeks: Temporal.PlainDate[][];
  /** First date rendered, which may belong to the previous month. */
  readonly rangeStart: Temporal.PlainDate;
  /** Last date rendered, which may belong to the next month. */
  readonly rangeEnd: Temporal.PlainDate;
  /** Column headers, ordered to match `weeks`. */
  readonly weekdayOrder: Weekday[];
}

/**
 * The visible month grid, padded out to whole weeks.
 *
 * The padding dates matter: the calendar must expand occurrences for the *whole
 * grid*, not just the month, or classes in the leading and trailing cells would
 * be silently missing.
 */
export function buildMonthGrid(
  yearMonth: Temporal.PlainYearMonth,
  weekStartsOn: Weekday = 7,
): MonthGrid {
  const firstOfMonth = yearMonth.toPlainDate({ day: 1 });
  const lastOfMonth = yearMonth.toPlainDate({ day: yearMonth.daysInMonth });

  const leading = (firstOfMonth.dayOfWeek - weekStartsOn + 7) % 7;
  const rangeStart = firstOfMonth.subtract({ days: leading });

  const trailing = (weekStartsOn - 1 - lastOfMonth.dayOfWeek + 7) % 7;
  const rangeEnd = lastOfMonth.add({ days: trailing });

  const totalDays = rangeStart.until(rangeEnd, { largestUnit: 'day' }).days + 1;
  const weeks: Temporal.PlainDate[][] = [];
  for (let index = 0; index < totalDays; index += 7) {
    const week: Temporal.PlainDate[] = [];
    for (let offset = 0; offset < 7; offset += 1) {
      week.push(rangeStart.add({ days: index + offset }));
    }
    weeks.push(week);
  }

  const weekdayOrder = Array.from(
    { length: 7 },
    (_unused, index) => (((weekStartsOn - 1 + index) % 7) + 1) as Weekday,
  );

  return { yearMonth, weeks, rangeStart, rangeEnd, weekdayOrder };
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `July 2026`, formatted without Intl so tests are locale-independent. */
export function formatYearMonth(yearMonth: Temporal.PlainYearMonth): string {
  return `${MONTH_NAMES[yearMonth.month - 1] ?? '?'} ${yearMonth.year}`;
}

export function formatMonthDay(date: Temporal.PlainDate): string {
  return `${MONTH_NAMES[date.month - 1] ?? '?'} ${date.day}`;
}

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** `Tue 28 Jul`, formatted by hand to stay locale-independent. */
export function formatDayStamp(date: Temporal.PlainDate): string {
  const weekday = WEEKDAY_ABBR[date.dayOfWeek - 1] ?? '?';
  return `${weekday} ${date.day} ${MONTH_ABBR[date.month - 1] ?? '?'}`;
}

/**
 * The heading for a day in the schedule list.
 *
 * "Today" and "Tomorrow" are named rather than dated because that is how the day
 * is actually thought about; everything else gets a short stamp. Relative to an
 * explicit `today` so the caller owns the clock and this stays pure.
 */
export function dayLabel(date: Temporal.PlainDate, today: Temporal.PlainDate): string {
  const stamp = formatDayStamp(date);
  const daysAway = today.until(date, { largestUnit: 'day' }).days;
  if (daysAway === 0) {
    return `Today · ${stamp}`;
  }
  if (daysAway === 1) {
    return `Tomorrow · ${stamp}`;
  }
  return stamp;
}
